import type { LeaveRequest } from "@spork/s4p-server-sdk";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pressable, ScrollView, View } from "react-native";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
    SheetModal,
    useSheetModalBodyMaxHeight,
} from "@/components/ui/sheet-modal";
import { Text } from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import { LeaveRequestAttachmentsPanel } from "@/features/requests/LeaveRequestAttachmentsPanel";
import { canSubmitLeaveRequestResubmit } from "@/features/requests/leaveRequestEvents";
import { TEST_IDS } from "@/testIdDefinition";

type ResubmitRequestCardProps = {
    visible: boolean;
    onClose: () => void;
    request: LeaveRequest;
    infoNote: string | null;
    isBusy: boolean;
    isResubmitting: boolean;
    onResubmit: (message: string) => Promise<void>;
    onAttachmentAdded: (id: number) => void;
    onAttachmentRemoved: (id: number) => void;
    onAttachmentsBusyChange: (busy: boolean) => void;
    formKey: string;
};

export function ResubmitRequestCard({
    visible,
    onClose,
    request,
    infoNote,
    isBusy,
    isResubmitting,
    onResubmit,
    onAttachmentAdded,
    onAttachmentRemoved,
    onAttachmentsBusyChange,
    formKey,
}: ResubmitRequestCardProps) {
    const { t } = useTranslation();

    return (
        <SheetModal
            visible={visible}
            onRequestClose={onClose}
            dismissible={!isBusy}
            maxHeightRatio={0.85}
            accessibilityLabel={t("leave.closeResubmitA11y")}
        >
            <View className="flex-row items-center justify-between border-b border-border px-4 py-3">
                <Text variant="label">{t("leave.actionNeeded")}</Text>
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("leave.closeResubmitA11y")}
                    onPress={onClose}
                    disabled={isBusy}
                    className="px-1 py-1 active:opacity-70"
                >
                    <Text className="text-primary">{t("leave.close")}</Text>
                </Pressable>
            </View>

            <ResubmitRequestForm
                key={formKey}
                request={request}
                infoNote={infoNote}
                isBusy={isBusy}
                isResubmitting={isResubmitting}
                onResubmit={onResubmit}
                onAttachmentAdded={onAttachmentAdded}
                onAttachmentRemoved={onAttachmentRemoved}
                onAttachmentsBusyChange={onAttachmentsBusyChange}
            />
        </SheetModal>
    );
}

type ResubmitRequestFormProps = {
    request: LeaveRequest;
    infoNote: string | null;
    isBusy: boolean;
    isResubmitting: boolean;
    onResubmit: (message: string) => Promise<void>;
    onAttachmentAdded: (id: number) => void;
    onAttachmentRemoved: (id: number) => void;
    onAttachmentsBusyChange: (busy: boolean) => void;
};

function ResubmitRequestForm({
    request,
    infoNote,
    isBusy,
    isResubmitting,
    onResubmit,
    onAttachmentAdded,
    onAttachmentRemoved,
    onAttachmentsBusyChange,
}: ResubmitRequestFormProps) {
    const { t } = useTranslation();
    const bodyMaxHeight = useSheetModalBodyMaxHeight();
    const [resubmitMessage, setResubmitMessage] = useState("");
    const canSubmit = canSubmitLeaveRequestResubmit(request, resubmitMessage);

    return (
        <ScrollView
            style={bodyMaxHeight != null ? { maxHeight: bodyMaxHeight } : undefined}
            keyboardShouldPersistTaps="handled"
            contentContainerClassName="gap-3 px-4 py-4"
        >
            <Alert icon="circle-info">
                <AlertTitle>{t("leave.schoolRequestTitle")}</AlertTitle>
                <AlertDescription
                    testID={
                        TEST_IDS[
                            TEST_IDS.REQUESTS_LIST_ITEM_RESUBMIT_SCHOOL_MESSAGE
                        ]
                    }
                >
                    {infoNote ?? t("leave.infoRequestedDefault")}
                </AlertDescription>
            </Alert>

            <Text variant="muted">{t("leave.resubmitHint")}</Text>

            <Textarea
                value={resubmitMessage}
                onChangeText={setResubmitMessage}
                placeholder={t("leave.resubmitPlaceholder")}
                editable={!isBusy}
                testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_RESUBMIT_INPUT]}
            />

            <LeaveRequestAttachmentsPanel
                request={request}
                onAdded={onAttachmentAdded}
                onRemoved={onAttachmentRemoved}
                onBusyChange={onAttachmentsBusyChange}
            />

            <Button
                disabled={isBusy || !canSubmit}
                onPress={() => {
                    void onResubmit(resubmitMessage);
                }}
                testID={TEST_IDS[TEST_IDS.REQUESTS_LIST_ITEM_RESUBMIT_BUTTON]}
            >
                <Text className="text-primary-foreground">
                    {isResubmitting
                        ? t("leave.resubmitting")
                        : t("leave.resubmit")}
                </Text>
            </Button>
        </ScrollView>
    );
}
